import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Zap, Flame, MapPin, ArrowRight, Check, Shield, PhoneCall } from 'lucide-react';

const faqs = [
  {
    q: 'Wo kann ich in Völklingen meinen Strom- oder Gasanbieter wechseln?',
    a: 'Bei Easy Vertrag in der Bismarck Straße 31, 66333 Völklingen. Wir vergleichen alle Strom- und Gasanbieter für Ihren Haushalt im Saarland kostenlos und übernehmen die komplette Abwicklung.',
  },
  {
    q: 'Welche Stromanbieter sind in Völklingen und dem Saarland verfügbar?',
    a: 'Im Saarland können Sie zwischen zahlreichen Stromanbietern wählen – von lokalen Stadtwerken bis zu bundesweiten Anbietern wie E.ON, Vattenfall, EnBW oder günstigen Online-Anbietern. Wir zeigen Ihnen alle verfügbaren Tarife.',
  },
  {
    q: 'Wie viel kann ich durch einen Anbieterwechsel im Saarland sparen?',
    a: 'Je nach Verbrauch und aktuellem Tarif sind Einsparungen von 200–800 € pro Jahr möglich. Viele Saarländer zahlen noch immer den teuren Grundversorgungstarif – ein Wechsel lohnt sich fast immer.',
  },
  {
    q: 'Wie lange dauert ein Strom- oder Gaswechsel im Saarland?',
    a: 'In der Regel dauert der Wechsel 4–6 Wochen. Wir kündigen Ihren alten Vertrag und melden Sie beim neuen Anbieter an – ohne Unterbrechung der Versorgung.',
  },
  {
    q: 'Kann ich Strom und Gas gleichzeitig wechseln?',
    a: 'Ja! Viele Anbieter bieten Kombi-Tarife für Strom und Gas an, die oft günstiger sind als zwei separate Verträge. Wir prüfen, ob ein Kombi-Tarif für Sie sinnvoll ist.',
  },
];

const StromGasVoelklingenPage: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Strom & Gas Tarife Saarland & Völklingen wechseln | Easy Vertrag</title>
        <meta name="description" content="Strom und Gas Tarife im Saarland vergleichen und wechseln - persönliche Beratung in Völklingen. Bis zu 800€ sparen. Easy Vertrag übernimmt den kompletten Wechsel. Jetzt kostenlos beraten!" />
        <meta name="keywords" content="Strom Gas Saarland, Strom Völklingen, Gastarif Saarland, Energieanbieter wechseln Saarland, Stromanbieter Völklingen, günstiger Strom Saar, Gas Tarif vergleichen" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/strom-gas-voelklingen" />
        <meta property="og:title" content="Strom & Gas Tarife Saarland vergleichen | Easy Vertrag Völklingen" />
        <meta property="og:description" content="Strom und Gas im Saarland günstig wechseln. Persönliche Beratung in Völklingen – bis zu 800€ sparen." />
        <meta property="og:url" content="https://easy4vertrag.de/strom-gas-voelklingen" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              ${faqs.map(f => `{
                "@type": "Question",
                "name": "${f.q}",
                "acceptedAnswer": {"@type": "Answer", "text": "${f.a}"}
              }`).join(',')}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Strom & Gas Saarland", "item": "https://easy4vertrag.de/strom-gas-voelklingen"}
            ]
          }
        `}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-green-700 to-green-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white bg-opacity-15 rounded-full px-4 py-2 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Bismarck Straße 31 · 66333 Völklingen · Saarland
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-5 leading-tight">
              Strom & Gas im Saarland günstiger machen
            </h1>
            <p className="text-xl text-green-100 mb-4">
              Energieanbieter in Völklingen und dem Saarland vergleichen und bis zu 800 € pro Jahr sparen. Wir übernehmen den kompletten Wechsel kostenlos.
            </p>
            <p className="text-green-200 mb-8 text-base">
              Persönliche Beratung in Völklingen · +49 179 7473491
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/strom-gas"
                className="inline-flex items-center justify-center px-7 py-3 bg-white text-green-700 font-semibold rounded-lg hover:bg-green-50 transition-colors"
              >
                Tarife jetzt vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a
                href="https://wa.me/491797473491"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-green-700 transition-colors"
              >
                WhatsApp Beratung
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Banner */}
      <section className="py-10 bg-yellow-50 border-y border-yellow-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6 text-center">
            {[
              { value: 'bis 800 €', label: 'Ersparnis pro Jahr möglich', icon: <Zap className="w-6 h-6 text-yellow-600" /> },
              { value: '100%', label: 'Kostenloser Wechselservice', icon: <Shield className="w-6 h-6 text-green-600" /> },
              { value: '4–6 Wo.', label: 'Wechsel abgeschlossen', icon: <Check className="w-6 h-6 text-blue-600" /> },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border">
                  {item.icon}
                </div>
                <p className="text-2xl font-bold text-gray-800">{item.value}</p>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">
              Strom & Gas wechseln in Völklingen – so einfach geht's
            </h2>
            <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
              Wir kümmern uns um alles – von der Tarifsuche bis zum fertigen Wechsel.
            </p>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { step: '1', title: 'Kontakt aufnehmen', text: 'Per WhatsApp, Telefon oder persönlich in Völklingen melden.' },
                { step: '2', title: 'Tarife vergleichen', text: 'Wir zeigen Ihnen alle günstigen Anbieter für Ihren Verbrauch.' },
                { step: '3', title: 'Anbieter wählen', text: 'Sie wählen den besten Tarif – wir klären alle Details.' },
                { step: '4', title: 'Wechsel abgeschlossen', text: 'Wir erledigen Kündigung und Anmeldung komplett für Sie.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-5 border border-gray-100 text-center relative">
                  <div className="w-9 h-9 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-3">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2 text-sm">{item.title}</h3>
                  <p className="text-gray-600 text-xs">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Strom vs Gas */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">
              Strom und Gas im Saarland vergleichen
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Zap className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Strom im Saarland</h3>
                </div>
                <ul className="space-y-2">
                  {[
                    'Alle Stromanbieter deutschlandweit verfügbar',
                    'Ökostromtarife und konventionelle Tarife',
                    'Jahresverträge und monatlich kündbar',
                    'Oft 100–500 € Ersparnis gegenüber Grundversorgung',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                      <Check className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/strom-gas" className="inline-flex items-center mt-5 text-yellow-700 font-semibold text-sm hover:underline">
                  Strom vergleichen <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </div>
              <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Flame className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Gas im Saarland</h3>
                </div>
                <ul className="space-y-2">
                  {[
                    'Günstige Gastarife für Haushalte im Saarland',
                    'Kombi-Tarife Strom + Gas oft am günstigsten',
                    'Lokale und bundesweite Gasanbieter',
                    'Oft 200–800 € Ersparnis pro Jahr möglich',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                      <Check className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/strom-gas" className="inline-flex items-center mt-5 text-orange-700 font-semibold text-sm hover:underline">
                  Gas vergleichen <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">
              Häufige Fragen zu Strom & Gas im Saarland
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-800 mb-2 text-base">{faq.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-green-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Jetzt Strom & Gas im Saarland wechseln</h2>
            <p className="text-green-100 mb-2">Easy Vertrag · Bismarck Straße 31 · 66333 Völklingen</p>
            <p className="text-green-200 text-sm mb-8">Mo–Fr 10:00–20:00 Uhr · Sa 10:00–18:00 Uhr · Kostenloser Wechselservice</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+491797473491"
                className="inline-flex items-center justify-center px-7 py-3 bg-white text-green-700 font-semibold rounded-lg hover:bg-green-50 transition-colors"
              >
                <PhoneCall className="w-5 h-5 mr-2" />
                Jetzt anrufen
              </a>
              <Link
                to="/strom-gas"
                className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-green-700 transition-colors"
              >
                Tarife vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StromGasVoelklingenPage;
